import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import api from '@/lib/api';
import { formatName } from '@/lib/utils';
import { ArrowLeft, Download, CreditCard, FileX, Pencil, Copy, Loader2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';

const STATUS_COLORS = {
  DRAFT: 'bg-gray-100 text-gray-700',
  UNPAID: 'bg-[#F5B041]/10 text-[#F5B041]',
  PARTIALLY_PAID: 'bg-[#0A6F64]/10 text-[#0A6F64]',
  PAID: 'bg-[#2E8B57]/10 text-[#2E8B57]',
  CANCELLED: 'bg-[#D3544E]/10 text-[#D3544E]',
};

export default function InvoiceDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'ADMIN';
  const [inv, setInv] = useState(null);
  const [showPay, setShowPay] = useState(false);
  const [showCN, setShowCN] = useState(false);
  const today = new Date().toISOString().split('T')[0];
  const [payForm, setPayForm] = useState({ amount: '', payment_mode: 'CASH', payment_date: today, reference: '', notes: '' });
  const [cnForm, setCnForm] = useState({ amount: '', reason: '' });
  const [payingSubmitting, setPayingSubmitting] = useState(false);
  const [payError, setPayError] = useState('');
  const [cnSubmitting, setCnSubmitting] = useState(false);
  const [cnError, setCnError] = useState('');
  const [finalizing, setFinalizing] = useState(false);
  const [duplicating, setDuplicating] = useState(false);
  // Synchronous in-flight guards (refs update immediately, unlike async state) to block double-submits
  const busyRef = useRef({ pay: false, cn: false, finalize: false, dup: false });

  const fetch = async () => {
    const r = await api.get(`/invoices/${id}`); setInv(r.data);
  };
  useEffect(() => { fetch(); }, [id]);

  if (!inv) return <div className="p-6 text-sm text-[#4A6B68]">Loading...</div>;

  const isDraft = !!inv.is_draft;
  const status = inv.status;
  const balance = inv.balance_due || 0;

  const downloadPdf = async () => {
    const res = await api.get(`/invoices/${id}/pdf`, { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement('a'); a.href = url; a.download = `invoice_${inv.invoice_number || id}.pdf`; a.click();
    window.URL.revokeObjectURL(url);
  };

  const recordPayment = async (e) => {
    e.preventDefault();
    if (busyRef.current.pay) return; // synchronous guard against double-submit
    busyRef.current.pay = true;
    setPayingSubmitting(true);
    setPayError('');
    const amt = parseFloat(payForm.amount);
    try {
      await api.post(`/invoices/${id}/payments`, { ...payForm, amount: amt });
      setShowPay(false);
      setPayForm({ amount: '', payment_mode: 'CASH', payment_date: today, reference: '', notes: '' });
      await fetch();
      toast.success(`Payment of INR ${amt.toLocaleString()} recorded successfully.`);
    } catch (err) {
      setPayError(err.response?.data?.detail || 'Payment could not be recorded. Please try again.');
    } finally {
      busyRef.current.pay = false;
      setPayingSubmitting(false);
    }
  };

  const issueCN = async (e) => {
    e.preventDefault();
    if (busyRef.current.cn) return;
    busyRef.current.cn = true;
    setCnSubmitting(true);
    setCnError('');
    try {
      await api.post(`/invoices/${id}/credit-note`, { ...cnForm, amount: parseFloat(cnForm.amount) });
      setShowCN(false);
      await fetch();
      toast.success('Invoice cancelled. Credit note issued successfully.');
    } catch (err) {
      setCnError(err.response?.data?.detail || 'Invoice could not be cancelled. Please try again.');
    } finally {
      busyRef.current.cn = false;
      setCnSubmitting(false);
    }
  };

  const finalize = async () => {
    if (busyRef.current.finalize) return;
    if (!window.confirm('Finalize this invoice? It cannot be edited after finalization.')) return;
    busyRef.current.finalize = true;
    setFinalizing(true);
    try {
      await api.post(`/invoices/${id}/finalize`);
      await fetch();
      toast.success('Invoice finalized successfully.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Finalize failed. Please try again.');
    } finally {
      busyRef.current.finalize = false;
      setFinalizing(false);
    }
  };

  const duplicate = async () => {
    if (busyRef.current.dup) return;
    busyRef.current.dup = true;
    setDuplicating(true);
    try {
      const r = await api.post(`/invoices/${id}/duplicate`);
      navigate(`/invoicing/invoice/${r.data._id}/edit`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Duplicate failed. Please try again.');
      busyRef.current.dup = false;
      setDuplicating(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="invoice-detail-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Button variant="ghost" onClick={() => navigate('/invoicing')} className="text-[#4A6B68]"><ArrowLeft className="w-4 h-4 mr-2" /> Back</Button>
          <h1 className="text-xl font-semibold text-[#1A3330] font-['Outfit']">{inv.invoice_number || 'DRAFT'}</h1>
          <Badge className={`${STATUS_COLORS[status] || ''} text-[11px] border-0`}>{status?.replace('_',' ')}</Badge>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button onClick={downloadPdf} variant="outline" className="rounded-xl border-[#E5F2F1]" data-testid="dl-pdf-btn"><Download className="w-4 h-4 mr-2" /> PDF</Button>
          {isDraft && (
            <>
              <Button onClick={() => navigate(`/invoicing/invoice/${id}/edit`)} variant="outline" className="rounded-xl border-[#E5F2F1]" data-testid="edit-inv-btn"><Pencil className="w-4 h-4 mr-2" /> Edit</Button>
              <Button onClick={finalize} disabled={finalizing} className="bg-[#0D8B7D] hover:bg-[#0B786C] text-white rounded-xl" data-testid="finalize-inv-btn">{finalizing ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Finalizing...</>) : 'Finalize'}</Button>
            </>
          )}
          {!isDraft && status !== 'CANCELLED' && balance > 0 && (
            <Button onClick={() => { setPayError(''); setPayForm({...payForm, amount: balance}); setShowPay(true); }} className="bg-[#0D8B7D] hover:bg-[#0B786C] text-white rounded-xl" data-testid="record-payment-btn"><CreditCard className="w-4 h-4 mr-2" /> Record Payment</Button>
          )}
          {isAdmin && status !== 'CANCELLED' && (
            <Button onClick={() => { setCnError(''); setCnForm({ amount: inv.grand_total, reason: '' }); setShowCN(true); }} variant="outline" className="rounded-xl border-[#E5F2F1] text-[#D3544E]" data-testid="cancel-invoice-btn"><FileX className="w-4 h-4 mr-2" /> Cancel Invoice</Button>
          )}
          <Button onClick={duplicate} disabled={duplicating} variant="outline" className="rounded-xl border-[#E5F2F1]" data-testid="duplicate-btn">{duplicating ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Duplicating...</>) : (<><Copy className="w-4 h-4 mr-2" /> Duplicate</>)}</Button>
        </div>
      </div>

      <Card className="bg-white border-[#E5F2F1] rounded-2xl">
        <CardContent className="p-5 space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
            <div><p className="text-[10px] uppercase text-[#4A6B68]">Client</p><p className="font-medium">{formatName(inv.client)}</p></div>
            <div><p className="text-[10px] uppercase text-[#4A6B68]">Branch</p><p className="font-medium">{inv.branch?.name || '-'}</p></div>
            <div><p className="text-[10px] uppercase text-[#4A6B68]">Date</p><p className="font-medium">{inv.invoice_date}</p></div>
            <div><p className="text-[10px] uppercase text-[#4A6B68]">Due</p><p className="font-medium">{inv.due_date || '-'}</p></div>
          </div>

          <div className="border border-[#E5F2F1] rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[#F4FAF9]"><tr>
                <th className="text-left p-2 text-xs text-[#4A6B68]">#</th>
                <th className="text-left p-2 text-xs text-[#4A6B68]">Item</th>
                <th className="text-left p-2 text-xs text-[#4A6B68]">SAC/HSN</th>
                <th className="text-right p-2 text-xs text-[#4A6B68]">Qty</th>
                <th className="text-right p-2 text-xs text-[#4A6B68]">Rate</th>
                <th className="text-right p-2 text-xs text-[#4A6B68]">Total</th>
              </tr></thead>
              <tbody>
                {(inv.lines || []).map((l, i) => (
                  <tr key={i} className="border-t border-[#E5F2F1]">
                    <td className="p-2 text-xs">{i+1}</td>
                    <td className="p-2"><p className="text-sm font-medium">{l.name}</p>{l.description && <p className="text-xs text-[#4A6B68]">{l.description}</p>}</td>
                    <td className="p-2 text-xs">{l.code || '-'}</td>
                    <td className="p-2 text-xs text-right">{l.quantity}</td>
                    <td className="p-2 text-xs text-right">Rs.{l.unit_rate?.toLocaleString()}</td>
                    <td className="p-2 text-sm text-right font-medium">Rs.{l.line_total?.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex justify-end">
            <div className="w-full sm:w-80 bg-[#F4FAF9] rounded-xl p-4 space-y-1 text-sm">
              <Row l="Subtotal" v={inv.subtotal} />
              {inv.line_discount_total > 0 && <Row l="Line Discounts" v={inv.line_discount_total} neg />}
              {inv.invoice_discount > 0 && <Row l="Invoice Discount" v={inv.invoice_discount} neg />}
              {inv.cgst_total > 0 && <Row l="CGST" v={inv.cgst_total} />}
              {inv.sgst_total > 0 && <Row l="SGST" v={inv.sgst_total} />}
              {Math.abs(inv.round_off || 0) > 0.001 && <Row l="Round Off" v={inv.round_off} />}
              <div className="pt-2 border-t border-[#0D8B7D]/20 flex justify-between"><span className="font-bold text-[#0D8B7D]">Grand Total</span><span className="text-xl font-bold text-[#0D8B7D]" data-testid="grand-total">Rs.{inv.grand_total?.toLocaleString()}</span></div>
              <div className="flex justify-between text-xs"><span className="text-[#4A6B68]">Total Paid</span><span className="text-[#2E8B57]">Rs.{(inv.total_paid||0).toLocaleString()}</span></div>
              {balance > 0 && <div className="flex justify-between text-sm font-semibold"><span className="text-[#D3544E]">Balance Due</span><span className="text-[#D3544E]" data-testid="balance-due">Rs.{balance.toLocaleString()}</span></div>}
            </div>
          </div>

          <p className="text-xs text-[#4A6B68] italic">{inv.amount_in_words}</p>
        </CardContent>
      </Card>

      {/* Payments */}
      <Card className="bg-white border-[#E5F2F1] rounded-2xl">
        <CardContent className="p-5">
          <h3 className="font-semibold text-[#1A3330] font-['Outfit'] mb-3">Payment History</h3>
          {(!inv.payments || inv.payments.length === 0) ? <p className="text-sm text-[#4A6B68]">No payments recorded yet.</p> : (
            <table className="w-full text-sm">
              <thead className="bg-[#F4FAF9]"><tr>
                <th className="text-left p-2 text-xs text-[#4A6B68]">Date</th>
                <th className="text-right p-2 text-xs text-[#4A6B68]">Amount</th>
                <th className="text-left p-2 text-xs text-[#4A6B68]">Mode</th>
                <th className="text-left p-2 text-xs text-[#4A6B68]">Reference</th>
                <th className="text-left p-2 text-xs text-[#4A6B68]">Invoice #</th>
              </tr></thead>
              <tbody>
                {(inv.payments || []).map(p => (
                  <tr key={p._id} className="border-t border-[#E5F2F1]" data-testid={`payment-row-${p._id}`}>
                    <td className="p-2 text-xs">{p.payment_date}</td>
                    <td className="p-2 text-sm text-right text-[#2E8B57] font-medium">Rs.{p.amount?.toLocaleString()}</td>
                    <td className="p-2 text-xs">{p.payment_mode}</td>
                    <td className="p-2 text-xs">{p.reference || '-'}</td>
                    <td className="p-2 text-xs">{p.invoice_number}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* Record Payment Modal */}
      <Dialog open={showPay} onOpenChange={(o) => { if (!payingSubmitting) setShowPay(o); }}>
        <DialogContent className="max-w-md bg-white rounded-2xl" data-testid="record-payment-modal">
          <DialogHeader><DialogTitle className="font-['Outfit']">Record Payment</DialogTitle></DialogHeader>
          <form onSubmit={recordPayment} className="space-y-3">
            <div className="bg-[#F4FAF9] rounded-xl p-3 text-sm">
              <p>Invoice: <strong>{inv.invoice_number}</strong></p>
              <p>Balance Due: <strong className="text-[#D3544E]">Rs.{balance.toLocaleString()}</strong></p>
            </div>
            <div><Label>Amount *</Label><Input type="number" step="0.01" value={payForm.amount} required onChange={e => setPayForm({...payForm, amount: e.target.value})} className="rounded-xl border-[#E5F2F1]" data-testid="pay-amount" /></div>
            <div><Label>Payment Mode</Label>
              <Select value={payForm.payment_mode} onValueChange={v => setPayForm({...payForm, payment_mode: v})}>
                <SelectTrigger className="rounded-xl border-[#E5F2F1]" data-testid="pay-mode"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="CASH">Cash</SelectItem><SelectItem value="UPI">UPI</SelectItem><SelectItem value="CARD">Card</SelectItem><SelectItem value="BANK_TRANSFER">Bank Transfer</SelectItem><SelectItem value="OTHER">Other</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label>Date</Label><Input type="date" value={payForm.payment_date} onChange={e => setPayForm({...payForm, payment_date: e.target.value})} className="rounded-xl border-[#E5F2F1]" /></div>
            <div><Label>Reference / Txn ID</Label><Input value={payForm.reference} onChange={e => setPayForm({...payForm, reference: e.target.value})} className="rounded-xl border-[#E5F2F1]" data-testid="pay-ref" /></div>
            <div><Label>Notes</Label><Textarea rows={2} value={payForm.notes} onChange={e => setPayForm({...payForm, notes: e.target.value})} className="rounded-xl border-[#E5F2F1]" /></div>
            {payError && <p className="text-sm text-[#D3544E]" data-testid="pay-error">{payError}</p>}
            <div className="flex gap-2 pt-2">
              <Button type="submit" disabled={payingSubmitting} className="flex-1 bg-[#0D8B7D] hover:bg-[#0B786C] text-white rounded-xl" data-testid="confirm-payment-btn">{payingSubmitting ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Recording...</>) : 'Record Payment'}</Button>
              <Button type="button" variant="outline" disabled={payingSubmitting} onClick={() => setShowPay(false)} className="rounded-xl border-[#E5F2F1]">Cancel</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Cancel Invoice Modal (issues a credit note for the cancellation) */}
      <Dialog open={showCN} onOpenChange={(o) => { if (!cnSubmitting) setShowCN(o); }}>
        <DialogContent className="max-w-md bg-white rounded-2xl" data-testid="cancel-invoice-modal">
          <DialogHeader><DialogTitle className="font-['Outfit'] text-[#D3544E]">Cancel Invoice</DialogTitle></DialogHeader>
          <form onSubmit={issueCN} className="space-y-3">
            <p className="text-xs text-[#4A6B68]">{isDraft ? 'Cancelling this draft will mark it as CANCELLED. ' : 'A credit note will be generated to void the invoice. '}This action cannot be undone.</p>
            <div><Label>Amount *</Label><Input type="number" step="0.01" value={cnForm.amount} required onChange={e => setCnForm({...cnForm, amount: e.target.value})} className="rounded-xl border-[#E5F2F1]" data-testid="cn-amount" /></div>
            <div><Label>Reason *</Label><Textarea rows={3} value={cnForm.reason} required onChange={e => setCnForm({...cnForm, reason: e.target.value})} className="rounded-xl border-[#E5F2F1]" data-testid="cn-reason" /></div>
            {cnError && <p className="text-sm text-[#D3544E]" data-testid="cn-error">{cnError}</p>}
            <div className="flex gap-2 pt-2">
              <Button type="submit" disabled={cnSubmitting} className="flex-1 bg-[#D3544E] hover:bg-[#C04440] text-white rounded-xl" data-testid="confirm-cn-btn">{cnSubmitting ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Cancelling...</>) : 'Cancel Invoice'}</Button>
              <Button type="button" variant="outline" disabled={cnSubmitting} onClick={() => setShowCN(false)} className="rounded-xl border-[#E5F2F1]">Close</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row({ l, v, neg }) {
  return <div className="flex justify-between"><span className="text-[#4A6B68]">{l}</span><span className={`font-medium ${neg ? 'text-[#D3544E]' : 'text-[#1A3330]'}`}>{neg ? '- ' : ''}Rs.{(v || 0).toLocaleString()}</span></div>;
}
